// Функция для преобразования worker в data согласно контракту VerticalPreviewCard
import {getSiteDuration} from "src/utils/date-locale";
import {
    buildSpecialtyList,
    formatPrice,
    getProfessionalRoleLabel
} from "src/components/profiles/previewCards/base-preview-card";

import { tierBadgeLabel, tierFor } from "src/constants/trade-options";
import { priceLine, trustItems } from "src/utils/trade-display";

const resolveVerified = (profile) => Boolean(
    profile?.isVerified ?? profile?.verified ?? (profile?.emailVerified && profile?.phoneVerified)
);

export const tradeCardFields = (details) => {
    if (!details) {
        return {};
    }
    const years = details.experienceYears;
    const tier = years === null || years === undefined ? null : tierFor(years);
    const trust = trustItems(details).map((item) => item.label).join(' · ');
    const hasModernRates = Boolean(details.rates && !details.rates.legacy);
    return {
        tierLabel: tier ? tierBadgeLabel(years) : null,
        tierColor: tier ? tier.color : null,
        trustLabel: trust || null,
        ...(hasModernRates ? { priceLabel: priceLine(details.rates) } : {})
    };
};

const resolvePro = (profile) => profile?.isPro === true || profile?.pro === true;

const computePriceInfo = (pricing, hourlyRate) => {
    const amount = pricing?.amount ?? hourlyRate ?? null;
    const type = pricing?.type || 'hourly';
    return { priceLabel: amount ? formatPrice(amount, type) : null, priceType: type };
};

export const mapWorkerToPreviewData = (worker, theme) => {
    const {
        id,
        businessName,
        name,
        avatar,
        specialties = [],
        address,
        hourlyRate,
        pricing,
        rating,
        reviewCount,
        busyUntil,
        registrationAt,
        status // если есть прямое поле статуса
    } = worker;

    // Форматирование адреса
    const formatAddress = (address) => {
        if (!address) {
            return '';
        }

        const fromPlaceName = (placeName) => {
            const placeParts = placeName.split(', ');
            if (placeParts.length >= 2) {
                return `${placeParts[0]}, ${placeParts[1].split(' ')[0]}`;
            }
            return placeName;
        };

        if (typeof address === 'string') {
            return fromPlaceName(address);
        }

        if (Object.keys(address).length === 0) {
            return '';
        }

        if (address?.location?.place_name) {
            return fromPlaceName(address.location.place_name);
        }

        if (address.place_name) {
            return fromPlaceName(address.place_name);
        }

        const parts = [address.city, address.state, address.zip].filter(Boolean);
        if (parts.length > 0) {
            return parts.join(', ');
        }

        return '';
    };

    // Форматирование специализаций
    const formatSpecialtyLabel = (specialties) => {
        if (specialties?.length > 0) {
            return specialties
                .filter(spec => spec)
                .slice(0, 3)
                .map(spec => spec.label || spec)
                .join(', ') + (specialties.length > 3 ? '...' : '');
        }
        return 'Specialist';
    };

    // Определение статуса
    const getStatusKey = () => {
        if (busyUntil) return 'busy';
        if (status) {
            const statusLower = status.toLowerCase();
            if (statusLower.includes('hidden')) return 'hidden';
            if (statusLower.includes('review')) return 'on_review';
            if (statusLower.includes('fix')) return 'fix_it';
        }
        return 'available';
    };

    const statusKey = getStatusKey();

    // Маппинг статус ключей в метки
    const statusLabels = {
        available: 'Available',
        busy: 'Busy',
        hidden: 'Hidden',
        on_review: 'On review',
        fix_it: 'Fix it'
    };

    // Форматирование цены (из последнего трейда)
    const { priceLabel, priceType } = computePriceInfo(pricing, hourlyRate);

    // Получение инициалов
    const getInitials = () => {
        const title = businessName || name || '';
        return title.charAt(0).toUpperCase();
    };

    // Форматирование рейтинга
    const ratingValue = Math.min(Math.max(Number(rating) || 0, 0), 5);
    const reviewsCount = Math.max(Number(reviewCount) || 0, 0);

    // Форматирование длительности регистрации
    const getRegistrationDuration = () => {
        if (!registrationAt) return null;
        try {
            const date = registrationAt.toDate ? registrationAt.toDate() : new Date(registrationAt);
            return getSiteDuration(date);
        } catch {
            return null;
        }
    };

    return {
        image: avatar || '/assets/avatars/defaultUser.jpg',
        title: businessName || name || 'Your trade title',
        specialtyLabel: formatSpecialtyLabel(specialties),
        specialtyList: buildSpecialtyList(specialties),
        roleLabel: getProfessionalRoleLabel(worker.professionalRole),
        isVerified: resolveVerified(worker),
        isPro: resolvePro(worker),
        locationLabel: formatAddress(address),
        priceLabel: priceLabel,
        priceType: priceType,
        ratingValue: ratingValue,
        ratingDisplay: ratingValue.toFixed(1),
        reviewsCount: reviewsCount,
        avatarInitial: getInitials(),
        registrationDuration: getRegistrationDuration(),
        statusKey: statusKey,
        statusLabel: statusLabels[statusKey] || 'Available',
        ...tradeCardFields(worker.tradeDetails)
    };
};


// Функция для преобразования specialist в data согласно контракту VerticalPreviewCard
export const mapSpecialistToPreviewData = (specialist, theme) => {
    const {
        id,
        businessName,
        name,
        avatar,
        specialties = [],
        specialtyLabels = [],
        address,
        hourlyRate,
        pricing,
        rating,
        reviewsLength,
        reviewCount,
        busyUntil,
        registrationAt,
        status,
        gallery,
        completedProjects,
        commonContacts,
        description,
        since
    } = specialist;

    // Форматирование адреса
    const formatAddress = (address) => {
        if (!address) {
            return '';
        }

        const fromPlaceName = (placeName) => {
            const placeParts = placeName.split(', ');
            if (placeParts.length >= 2) {
                return `${placeParts[0]}, ${placeParts[1].split(' ')[0]}`;
            }
            return placeName;
        };

        if (typeof address === 'string') {
            return fromPlaceName(address);
        }

        if (Object.keys(address).length === 0) {
            return '';
        }

        if (address?.location?.place_name) {
            return fromPlaceName(address.location.place_name);
        }

        if (address.place_name) {
            return fromPlaceName(address.place_name);
        }

        const parts = [address.city, address.state, address.zip].filter(Boolean);
        if (parts.length > 0) {
            return parts.join(', ');
        }

        return '';
    };

    // Форматирование специализаций (используем specialtyLabels если есть, иначе specialties)
    const formatSpecialtyLabel = () => {
        if (specialtyLabels?.length > 0) {
            return specialtyLabels
                .filter(spec => spec)
                .slice(0, 3)
                .join(', ') + (specialtyLabels.length > 3 ? '...' : '');
        }

        if (specialties?.length > 0) {
            return specialties
                .filter(spec => spec)
                .slice(0, 3)
                .map(spec => spec.label || spec)
                .join(', ') + (specialties.length > 3 ? '...' : '');
        }

        return 'Specialist';
    };

    // Определение статуса
    const getStatusKey = () => {
        if (busyUntil) return 'busy';
        if (status) {
            const statusLower = status.toLowerCase();
            if (statusLower.includes('hidden')) return 'hidden';
            if (statusLower.includes('review')) return 'on_review';
            if (statusLower.includes('fix')) return 'fix_it';
        }
        return 'available';
    };

    const statusKey = getStatusKey();

    // Маппинг статус ключей в метки
    const statusLabels = {
        available: 'Available',
        busy: 'Busy',
        hidden: 'Hidden',
        on_review: 'On review',
        fix_it: 'Fix it'
    };

    // Форматирование цены (из последнего трейда)
    const { priceLabel, priceType } = computePriceInfo(pricing, hourlyRate);

    // Получение инициалов
    const getInitials = () => {
        const title = businessName || name || '';
        return title.charAt(0).toUpperCase();
    };

    // Форматирование рейтинга
    const ratingValue = Math.min(Math.max(Number(rating) || 0, 0), 5);
    const reviewsCount = Math.max(Number(reviewsLength || reviewCount) || 0, 0);

    // Форматирование длительности регистрации
    const getRegistrationDuration = () => {
        if (!registrationAt && !since) return null;
        try {
            const date = registrationAt?.toDate
                ? registrationAt.toDate()
                : since
                    ? new Date(since)
                    : new Date(registrationAt);
            return getSiteDuration(date);
        } catch {
            return null;
        }
    };

    // Дополнительные поля, которые могут быть полезны
    const additionalData = {
        completedProjects: completedProjects || 0,
        commonContacts: commonContacts || 0,
        description: description || '',
        gallery: gallery || [],
        hasGallery: gallery?.length > 0
    };

    return {
        image: avatar || '/assets/avatars/defaultUser.jpg',
        title: businessName || name || 'Your trade title',
        specialtyLabel: formatSpecialtyLabel(),
        specialtyList: buildSpecialtyList(specialtyLabels?.length ? specialtyLabels : specialties),
        roleLabel: getProfessionalRoleLabel(specialist.professionalRole),
        isVerified: resolveVerified(specialist),
        isPro: resolvePro(specialist),
        locationLabel: formatAddress(address),
        priceLabel: priceLabel,
        priceType: priceType,
        ratingValue: ratingValue,
        ratingDisplay: ratingValue.toFixed(1),
        reviewsCount: reviewsCount,
        avatarInitial: getInitials(),
        registrationDuration: getRegistrationDuration(),
        statusKey: statusKey,
        statusLabel: statusLabels[statusKey] || 'Available',
        // Дополнительные поля для расширенного использования
        ...additionalData
    };
};